import {
  atan,
  attribute,
  clamp,
  dot,
  float,
  mix,
  modelWorldMatrix,
  positionGeometry,
  positionLocal,
  smoothstep,
  vec3,
  vec4,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import { registerVolumeMaterial, sceneSurfacePosition } from '../render/lightVolumes';
import { buildTreeGeometry } from '../vegetation/TreeRenderer';
import { CRYSTAL, GLOW_STRIDE } from './caveProps';

type N = any;

/** Most crystals, fungi (and lights) one cave can show. */
const CAPACITY = 800;
/** Brightness of a prop's light on the rock around it. */
const LIGHT_GAIN = 1.1;

const _m = new THREE.Matrix4();
const _x = new THREE.Vector3();
const _y = new THREE.Vector3();
const _z = new THREE.Vector3();
const _p = new THREE.Vector3();

/**
 * A cave's crystal clusters and glowing fungi (placed by decorateChunk): one instanced mesh
 * of each, and a light volume for each prop in the deferred light pass, so they light the
 * rock around them. Filled as the cave's chunks are built, emptied when it's dropped.
 * Each keeps one invisible instance, so their shaders compile with everything else before
 * any cave is built. Everything is relative to the cave's origin: put `group` in the
 * interior's (anchored) group, and anchor `lights`, which goes in the light scene.
 */
export class CaveGlow {
  readonly group = new THREE.Group();
  readonly lights = new THREE.Group();
  private readonly meshes: [THREE.InstancedMesh, THREE.InstancedMesh];
  private readonly volumes: THREE.InstancedBufferGeometry;
  private readonly counts = [1, 1];

  constructor(lightsNode: THREE.LightsNode) {
    this.meshes = [propMesh('crystal', lightsNode), propMesh('mushroom', lightsNode)];
    for (const m of this.meshes) this.group.add(m);
    this.volumes = lightGeometry();
    const volume = new THREE.Mesh(this.volumes, lightMaterial());
    volume.frustumCulled = false;
    this.lights.add(volume);
    this.group.name = 'cave glow';
    this.lights.name = 'cave glow lights';
  }

  /** Add props (GLOW_STRIDE numbers each: kind, position, up, scale, colour). */
  add(records: Float32Array): void {
    const centers = this.volumes.getAttribute('aCenter') as THREE.InstancedBufferAttribute;
    const radii = this.volumes.getAttribute('aRadius') as THREE.InstancedBufferAttribute;
    const lightGlow = this.volumes.getAttribute('aGlow') as THREE.InstancedBufferAttribute;
    for (let r = 0; r < records.length; r += GLOW_STRIDE) {
      const kind = records[r] === CRYSTAL ? 0 : 1;
      const i = this.counts[kind];
      const l = this.volumes.instanceCount;
      if (i >= CAPACITY || l >= CAPACITY) continue;
      _p.set(records[r + 1], records[r + 2], records[r + 3]);
      _y.set(records[r + 4], records[r + 5], records[r + 6]);
      const s = records[r + 7];
      // A right-handed frame around "up", turned by a number hashed from where it stands.
      const turn = fract(Math.sin(_p.x * 12.9898 + _p.y * 78.233 + _p.z * 37.719) * 43758.5453) * Math.PI * 2;
      _x.set(Math.abs(_y.y) < 0.9 ? 0 : 1, Math.abs(_y.y) < 0.9 ? 1 : 0, 0)
        .cross(_y)
        .normalize();
      _z.crossVectors(_x, _y);
      _x.multiplyScalar(Math.cos(turn)).addScaledVector(_z, Math.sin(turn));
      _z.crossVectors(_x, _y);
      _m.makeBasis(_x.multiplyScalar(s), _y.clone().multiplyScalar(s), _z.multiplyScalar(s));
      _m.setPosition(_p.clone().addScaledVector(_y, -0.1 * s));
      const mesh = this.meshes[kind];
      mesh.setMatrixAt(i, _m);
      const glow = mesh.geometry.getAttribute('aGlow') as THREE.InstancedBufferAttribute;
      glow.setXYZ(i, records[r + 8], records[r + 9], records[r + 10]);
      this.counts[kind] = i + 1;
      mesh.count = i + 1;
      mesh.instanceMatrix.needsUpdate = true;
      glow.needsUpdate = true;
      // Its light: at the crystals' middle, or under a mushroom's cap.
      const height = kind === 0 ? 1.8 : 5.0;
      centers.setXYZ(l, _p.x + _y.x * height * s, _p.y + _y.y * height * s, _p.z + _y.z * height * s);
      radii.setX(l, (kind === 0 ? 6 : 8) * s + 2);
      lightGlow.setXYZ(l, records[r + 8], records[r + 9], records[r + 10]);
      this.volumes.instanceCount = l + 1;
    }
    centers.needsUpdate = true;
    radii.needsUpdate = true;
    lightGlow.needsUpdate = true;
  }

  /** Remove every prop (the invisible ones stay). */
  clear(): void {
    for (const m of this.meshes) m.count = 1;
    this.counts[0] = this.counts[1] = 1;
    this.volumes.instanceCount = 1;
  }
}

/** A crystal cluster or a glowing mushroom (TreeRenderer's shapes), instanced, with a colour per instance. */
function propMesh(shape: 'crystal' | 'mushroom', lightsNode: THREE.LightsNode): THREE.InstancedMesh {
  const src = buildTreeGeometry(shape);
  const geo = new THREE.BufferGeometry();
  for (const name of ['position', 'normal', 'aPart']) geo.setAttribute(name, src.getAttribute(name));
  geo.setIndex(src.getIndex());
  geo.setAttribute('aGlow', new THREE.InstancedBufferAttribute(new Float32Array(CAPACITY * 3), 3));
  const crystal = shape === 'crystal';
  const mat = new THREE.MeshStandardNodeMaterial({ roughness: crystal ? 0.3 : 0.75, metalness: 0 });
  // The geometry's corners are shared (smooth normals): crystals take their facets from the
  // fragment shader.
  mat.flatShading = crystal;
  mat.lightsNode = lightsNode;
  const part: N = attribute('aPart', 'float');
  const glow: N = attribute('aGlow', 'vec3');
  const y: N = positionGeometry.y;
  if (crystal) {
    // A dark stone base and glowing spires, brightest at their tips.
    const spire = smoothstep(1.5, 2, part);
    mat.colorNode = mix(vec3(0.16, 0.15, 0.15), glow.mul(0.3).add(0.08), spire);
    mat.emissiveNode = glow.mul(spire.mul(smoothstep(0, 4.2, y).mul(1.1).add(0.35)));
  } else {
    // A pale stalk, a dusky cap with a faint glow at its rim, and glowing gills underneath.
    const cap = smoothstep(0.5, 1, part).sub(smoothstep(1.5, 2, part));
    const gills = smoothstep(1.5, 2, part);
    const r = positionGeometry.xz.length();
    const rim = smoothstep(2.4, 3.6, r);
    const stripes = atan(positionGeometry.z, positionGeometry.x).mul(22).sin().mul(0.35).add(0.65);
    const capColor = mix(vec3(0.2, 0.17, 0.2), glow.mul(0.22), rim);
    mat.colorNode = mix(mix(vec3(0.55, 0.5, 0.45), capColor, cap), glow.mul(0.3), gills);
    mat.emissiveNode = glow.mul(
      gills
        .mul(stripes)
        .mul(float(1).sub(r.div(4)).mul(1.2))
        .add(cap.mul(rim).mul(0.25)),
    );
  }
  const mesh = new THREE.InstancedMesh(geo, mat, CAPACITY);
  mesh.setMatrixAt(0, _m.makeScale(0, 0, 0));
  mesh.count = 1;
  mesh.frustumCulled = false;
  mesh.name = `cave ${shape}s`;
  return mesh;
}

/** Octahedra circumscribing unit spheres, one per light: centre, radius and colour per instance. */
function lightGeometry(): THREE.InstancedBufferGeometry {
  const oct = new THREE.OctahedronGeometry(Math.sqrt(3), 0);
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', oct.getAttribute('position'));
  g.setAttribute('aCenter', new THREE.InstancedBufferAttribute(new Float32Array(CAPACITY * 3), 3));
  g.setAttribute('aRadius', new THREE.InstancedBufferAttribute(new Float32Array(CAPACITY), 1));
  g.setAttribute('aGlow', new THREE.InstancedBufferAttribute(new Float32Array(CAPACITY * 3), 3));
  g.instanceCount = 1;
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e9);
  return g;
}

/**
 * Deferred point light: find the scene surface behind this pixel and light it by its
 * distance to the prop (inverse square, windowed to the sphere).
 */
function lightMaterial(): THREE.MeshBasicNodeMaterial {
  const mat = new THREE.MeshBasicNodeMaterial({
    transparent: true,
    depthTest: false,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.BackSide,
  });
  const center: N = attribute('aCenter', 'vec3');
  const radius: N = attribute('aRadius', 'float');
  mat.positionNode = center.add(positionLocal.mul(radius));
  const c = modelWorldMatrix.mul(vec4(center, 1)).xyz;
  const toLight = sceneSurfacePosition().sub(c);
  const d2 = dot(toLight, toLight);
  const w = clamp(float(1).sub(d2.div(radius.mul(radius).max(1e-4))), 0, 1);
  // The prop itself glows already: light only what's around it.
  const clear = smoothstep(0.2, 1.5, d2);
  const glow: N = attribute('aGlow', 'vec3');
  mat.colorNode = vec4(glow.mul(LIGHT_GAIN).div(d2.add(1.5)).mul(w.mul(w)).mul(clear), 1);
  registerVolumeMaterial(mat);
  return mat;
}

function fract(x: number): number {
  return x - Math.floor(x);
}
